export type CompressedSurgicalPlanImage = {
  dataUrl: string;
  mimeType: "image/jpeg";
  width: number;
  height: number;
  sizeBytes: number;
};

type CompressionOptions = {
  maxDimension?: number;
  quality?: number;
  targetBytes?: number;
};

const getBase64Bytes = (dataUrl: string) => {
  const base64 = String(dataUrl || "").replace(/^data:[^;]+;base64,/, "");
  return Math.max(0, Math.floor((base64.length * 3) / 4));
};

const loadImage = (dataUrl: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Nao foi possivel carregar a imagem para compressao."));
    image.src = dataUrl;
  });

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Nao foi possivel ler a imagem."));
    reader.readAsDataURL(file);
  });

const calculateSize = (width: number, height: number, maxDimension: number) => {
  const longestSide = Math.max(width, height);
  if (!longestSide || longestSide <= maxDimension) {
    return { width, height };
  }

  const scale = maxDimension / longestSide;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

export const compressImageForSurgicalPlan = async (
  fileOrDataUrl: File | string,
  options: CompressionOptions = {}
): Promise<CompressedSurgicalPlanImage> => {
  const sourceDataUrl =
    typeof fileOrDataUrl === "string" ? fileOrDataUrl : await readFileAsDataUrl(fileOrDataUrl);

  if (!sourceDataUrl.startsWith("data:image/")) {
    throw new Error("Envie uma imagem em JPG, PNG ou WEBP para o Plano Cirurgico.");
  }

  const image = await loadImage(sourceDataUrl);
  const maxDimension = options.maxDimension ?? 1024;
  const preferredQuality = options.quality ?? 0.7;
  const targetBytes = options.targetBytes ?? 180_000;
  const dimensions = [maxDimension, 900, 768, 640].filter(
    (value, index, list) => value > 0 && list.indexOf(value) === index
  );
  const qualities = [preferredQuality, 0.72, 0.68, 0.65].filter(
    (value, index, list) => value >= 0.65 && value <= 0.75 && list.indexOf(value) === index
  );

  let best: CompressedSurgicalPlanImage | null = null;

  for (const dimension of dimensions) {
    const size = calculateSize(image.naturalWidth || image.width, image.naturalHeight || image.height, dimension);
    const canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) {
      throw new Error("Nao foi possivel preparar a imagem para compressao.");
    }

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size.width, size.height);
    ctx.drawImage(image, 0, 0, size.width, size.height);

    for (const quality of qualities) {
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      const compressed: CompressedSurgicalPlanImage = {
        dataUrl,
        mimeType: "image/jpeg",
        width: size.width,
        height: size.height,
        sizeBytes: getBase64Bytes(dataUrl),
      };

      if (!best || compressed.sizeBytes < best.sizeBytes) {
        best = compressed;
      }

      if (compressed.sizeBytes <= targetBytes) {
        return compressed;
      }
    }
  }

  if (!best) {
    throw new Error("Nao foi possivel comprimir a imagem.");
  }

  return best;
};

export const estimateDataUrlBytes = getBase64Bytes;
